import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from './store';
import { apiString as API_URL } from '../utils/constants';
import axios from 'axios';

export interface TeamChpAiCitationDto {
    title?: string;
    source?: string;
    url?: string;
    excerpt?: string;
}

export interface TeamMemberSummaryTileDto {
    memberId: string;
    firstName: string;
    lastName: string;
    age: number;
    gender: string;
    riskLevel: string;
    status: string;
    healthPlanName: string;
    urgencyScore: number;
    rank: number;
    identifiedConcerns: string[];
    summaryText: string;
    mostRecentNoteDate?: string;
    generatedAt?: string;
    citations: TeamChpAiCitationDto[];
}

export interface TeamAiSummaryDto {
    id: string;
    teamId: string;
    markdown: string;
    citations: TeamChpAiCitationDto[];
    memberTiles: TeamMemberSummaryTileDto[];
    generatedAt: string;
    generatedByModel: string;
    promptTokens: number;
    completionTokens: number;
    inputHash: string;
    isStale: boolean;
}

export interface TeamAiSummaryProgressDto {
    stage: string;
    isComplete: boolean;
    hasError: boolean;
}

export interface TeamAiSummaryState {
    summary: TeamAiSummaryDto | null;
    progress: TeamAiSummaryProgressDto | null;
    isLoading: boolean;
    isGenerating: boolean;
    error: string | null;
}

const initialState: TeamAiSummaryState = {
    summary: null,
    progress: null,
    isLoading: false,
    isGenerating: false,
    error: null,
};

const teamAiSummarySlice = createSlice({
    name: 'teamAiSummary',
    initialState,
    reducers: {
        setSummary: (state, action: PayloadAction<TeamAiSummaryDto | null>) => {
            state.summary = action.payload;
        },
        setProgress: (state, action: PayloadAction<TeamAiSummaryProgressDto | null>) => {
            state.progress = action.payload;
        },
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setIsGenerating: (state, action: PayloadAction<boolean>) => {
            state.isGenerating = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        resetState: (state) => {
            state.summary = null;
            state.progress = null;
            state.isLoading = false;
            state.isGenerating = false;
            state.error = null;
        },
    },
});

export const { setSummary, setProgress, setIsLoading, setIsGenerating, setError, resetState } = teamAiSummarySlice.actions;

// --- Thunks ---

export const fetchTeamSummary = (teamId: string): AppThunk => async (dispatch) => {
    dispatch(setIsLoading(true));
    dispatch(setError(null));
    dispatch(setSummary(null));
    dispatch(setProgress(null));
    try {
        const response = await axios.get(`${API_URL}/teamsummary/cached/${teamId}`);
        if (response.status === 200 && response.data) {
            const s: TeamAiSummaryDto = {
                id: response.data.Id ?? response.data.id,
                teamId: response.data.TeamId ?? response.data.teamId,
                markdown: response.data.Markdown ?? response.data.markdown ?? '',
                citations: (response.data.Citations ?? response.data.citations ?? []).map((c: any) => ({
                    title: c.Title ?? c.title,
                    source: c.Source ?? c.source,
                    url: c.Url ?? c.url,
                    excerpt: c.Excerpt ?? c.excerpt,
                })),
                memberTiles: (response.data.MemberTiles ?? response.data.memberTiles ?? []).map((t: any) => ({
                    memberId: t.MemberId ?? t.memberId,
                    firstName: t.FirstName ?? t.firstName,
                    lastName: t.LastName ?? t.lastName,
                    age: t.Age ?? t.age ?? 0,
                    gender: t.Gender ?? t.gender ?? '',
                    riskLevel: t.RiskLevel ?? t.riskLevel ?? '',
                    status: t.Status ?? t.status ?? '',
                    healthPlanName: t.HealthPlanName ?? t.healthPlanName ?? '',
                    urgencyScore: t.UrgencyScore ?? t.urgencyScore ?? 0,
                    rank: t.Rank ?? t.rank ?? 0,
                    identifiedConcerns: t.IdentifiedConcerns ?? t.identifiedConcerns ?? [],
                    summaryText: t.SummaryText ?? t.summaryText ?? '',
                    mostRecentNoteDate: t.MostRecentNoteDate ?? t.mostRecentNoteDate,
                    generatedAt: t.GeneratedAt ?? t.generatedAt,
                    citations: (t.Citations ?? t.citations ?? []).map((c: any) => ({
                        title: c.Title ?? c.title,
                        source: c.Source ?? c.source,
                        url: c.Url ?? c.url,
                        excerpt: c.Excerpt ?? c.excerpt,
                    })),
                })),
                generatedAt: response.data.GeneratedAt ?? response.data.generatedAt,
                generatedByModel: response.data.GeneratedByModel ?? response.data.generatedByModel,
                promptTokens: response.data.PromptTokens ?? response.data.promptTokens ?? 0,
                completionTokens: response.data.CompletionTokens ?? response.data.completionTokens ?? 0,
                inputHash: response.data.InputHash ?? response.data.inputHash ?? '',
                isStale: response.data.IsStale ?? response.data.isStale ?? false,
            };
            dispatch(setSummary(s));
        } else {
            dispatch(setSummary(null));
        }
    } catch (e: any) {
        dispatch(setError(e.response?.data || 'Failed to fetch team summary.'));
        dispatch(setSummary(null));
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const triggerTeamSummaryGeneration = (teamId: string, force: boolean = false): AppThunk => async (dispatch) => {
    dispatch(setIsGenerating(true));
    dispatch(setError(null));
    try {
        await axios.post(`${API_URL}/teamsummary/generate/${teamId}?force=${force}`);
        dispatch(pollTeamSummaryProgress(teamId));
    } catch (e: any) {
        dispatch(setError(e.response?.data || 'Failed to trigger team summary generation.'));
        dispatch(setIsGenerating(false));
    }
};

// ponytail: per-team poll state in a Map, so multiple teams can poll concurrently.
// Single shared globals would clobber each other's interval.
const pollStates = new Map<string, { interval: ReturnType<typeof setInterval> | null; attempts: number }>();
const POLL_INTERVAL_MS = 1500;
const POLL_MAX_ATTEMPTS = 200;

export const pollTeamSummaryProgress = (teamId: string): AppThunk => async (dispatch) => {
    const existing = pollStates.get(teamId);
    if (existing?.interval) clearInterval(existing.interval);

    const state = { interval: null as ReturnType<typeof setInterval> | null, attempts: 0 };
    pollStates.set(teamId, state);

    const stopPolling = () => {
        if (state.interval) {
            clearInterval(state.interval);
            state.interval = null;
        }
        pollStates.delete(teamId);
    };

    const checkProgress = async () => {
        state.attempts++;
        if (state.attempts > POLL_MAX_ATTEMPTS) {
            stopPolling();
            dispatch(setIsGenerating(false));
            dispatch(setError('Team summary generation is taking longer than expected. Please try again.'));
            return;
        }
        try {
            const res = await axios.get(`${API_URL}/teamsummary/progress/${teamId}`);
            if (res.data) {
                const p: TeamAiSummaryProgressDto = {
                    stage: res.data.Stage ?? res.data.stage ?? 'Synthesizing...',
                    isComplete: res.data.IsComplete ?? res.data.isComplete ?? false,
                    hasError: res.data.HasError ?? res.data.hasError ?? false,
                };
                dispatch(setProgress(p));

                if (p.isComplete || p.hasError) {
                    stopPolling();
                    dispatch(setIsGenerating(false));
                    if (p.hasError) {
                        dispatch(setError(p.stage || 'Team summary generation failed.'));
                    }
                    dispatch(fetchTeamSummary(teamId));
                }
            }
        } catch {
            // keep polling; attempt counter bounds total tries.
        }
    };

    await checkProgress();
    state.interval = setInterval(checkProgress, POLL_INTERVAL_MS);
};

export const stopTeamSummaryPolling = (): AppThunk => () => {
    pollStates.forEach((state, teamId) => {
        if (state.interval) clearInterval(state.interval);
        pollStates.delete(teamId);
    });
};

export default teamAiSummarySlice.reducer;
